import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Download, Printer } from "lucide-react";
import {
  BUDGET_LINE_KEYS,
  ELIGIBILITY_ITEMS,
  SUPPORT_LABELS,
} from "./projectedRetreatConstants";
import {
  actorPayload,
  coupleEmails,
  coupleShortName,
  formatCurrency,
  formatWeekendRange,
  parishAddressLine,
  parishDisplayName,
  exportProjectedRetreatReport,
} from "./projectedRetreatHelpers";
import {
  ReportDetailRow,
  ReportSectionCard,
  fieldSx,
  projectedPageFontSx,
  projectedSaveButtonSx,
  HandoffSummaryCard,
} from "./projectedRetreatUi";
import type { ProjectedRetreatFormModel } from "./useProjectedRetreatForm";
import { toast } from "react-toastify";
import { useEmailDeliverability } from "../../../../hooks/useEmailDeliverability";
import EmailDeliverabilityStatus from "../../../../components/EmailDeliverabilityStatus";
import { shouldBlockOnInvalid } from "../../../../utils/emailDeliverability";

type FormModel = ProjectedRetreatFormModel;

export function ProjectedRetreatModals({ form }: { form: FormModel }) {
  const {
    transitioning,
    venue,
    language,
    retreatType,
    season,
    year,
    tentativeFriday,
    tentativeSunday,
    supportType,
    planningNotes,
    seedStatus,
    seedAmount,
    sponsorshipStatus,
    sponsorshipContact,
    sponsorshipExecution,
    sponsorshipNote,
    seedFundingContact,
    seedExecutionContact,
    seedNote,
    budgetNote,
    budget,
    eligibility,
    transitionNote,
    dirty,
    retreatId,
    newParishModalOpen,
    setNewParishModalOpen,
    newParishSaving,
    newParishForm,
    setNewParishForm,
    newParishErrors,
    setNewParishErrors,
    leadContactModalOpen,
    setLeadContactModalOpen,
    leadContactSaving,
    leadContactForm,
    setLeadContactForm,
    reportModalOpen,
    setReportModalOpen,
    setupConfirmModalOpen,
    setSetupConfirmModalOpen,
    selectedParish,
    selectedLead,
    showSeedFunding,
    showOutreachBudget,
    budgetTotal,
    eligibilityCount,
    primarySupport,
    primarySupportName,
    resolveSupportEmail,
    handleSaveNewParish,
    handleSaveLeadContact,
    handleConfirmTransition,
  } = form;

  const hisEmailStatus = useEmailDeliverability(leadContactForm.hisEmail);
  const herEmailStatus = useEmailDeliverability(leadContactForm.herEmail);

  const weekendLabel =
    tentativeFriday && tentativeSunday
      ? formatWeekendRange(tentativeFriday, tentativeSunday)
      : "Exact weekend remains tentative";
  const seasonYearLabel = season && year ? `${season} ${year}` : "";
  const recipientEmails =
    [coupleEmails(selectedLead), primarySupport ? resolveSupportEmail(primarySupport) : ""]
      .filter(Boolean)
      .join(" · ") || "Recipient contact information is unavailable.";
  const actorName = actorPayload().actorName;
  const [printingReport, setPrintingReport] = React.useState(false);

  const dialogPaperProps = {
    sx: {
      ...projectedPageFontSx,
      borderRadius: 4,
      boxShadow: "0 20px 40px -10px rgba(15, 23, 42, 0.2)",
    },
  };

  return (
    <>
      {/* Add Parish Modal */}
      <Dialog
        open={newParishModalOpen}
        onClose={() => setNewParishModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a", pb: 1 }}>
          Add New Parish
        </DialogTitle>
        <DialogContent dividers sx={{ borderTopColor: "#e2e8f0", borderBottomColor: "#e2e8f0" }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Parish name"
              value={newParishForm.name}
              error={!!newParishErrors.name}
              helperText={newParishErrors.name}
              onChange={(e) =>
                setNewParishForm((p) => ({ ...p, name: e.target.value }))
              }
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Address"
              value={newParishForm.addressLine1}
              error={!!newParishErrors.addressLine1}
              helperText={newParishErrors.addressLine1}
              onChange={(e) =>
                setNewParishForm((p) => ({
                  ...p,
                  addressLine1: e.target.value,
                }))
              }
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="City"
              value={newParishForm.city}
              error={!!newParishErrors.city}
              helperText={newParishErrors.city}
              onChange={(e) =>
                setNewParishForm((p) => ({ ...p, city: e.target.value }))
              }
              fullWidth
              sx={fieldSx}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="State"
                value={newParishForm.state}
                onChange={(e) =>
                  setNewParishForm((p) => ({ ...p, state: e.target.value }))
                }
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="ZIP"
                value={newParishForm.postalCode}
                onChange={(e) =>
                  setNewParishForm((p) => ({
                    ...p,
                    postalCode: e.target.value,
                  }))
                }
                fullWidth
                sx={fieldSx}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setNewParishModalOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={newParishSaving}
            onClick={() => handleSaveNewParish(false)}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2.5,
              px: 3,
              bgcolor: "#2563eb",
              "&:hover": { bgcolor: "#1d4ed8" },
            }}
          >
            {newParishSaving ? "Saving…" : "Create Parish"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Contact */}
      <Dialog
        open={leadContactModalOpen}
        onClose={() => setLeadContactModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a", pb: 1 }}>
          Update Lead Couple Contact
        </DialogTitle>
        <DialogContent dividers sx={{ borderTopColor: "#e2e8f0", borderBottomColor: "#e2e8f0" }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(
              [
                ["hisFirstName", "His First Name"],
                ["hisLastName", "His Last Name"],
                ["hisEmail", "His Email"],
                ["herFirstName", "Her First Name"],
                ["herLastName", "Her Last Name"],
                ["herEmail", "Her Email"],
                ["notes", "Contact preference or relevant note"],
                ["address", "Mailing address"],
              ] as const
            ).map(([key, label]) => (
              <Box key={key}>
                <TextField
                  label={label}
                  value={leadContactForm[key]}
                  onChange={(e) =>
                    setLeadContactForm((p) => ({ ...p, [key]: e.target.value }))
                  }
                  fullWidth
                  multiline={key === "notes" || key === "address"}
                  minRows={key === "notes" || key === "address" ? 2 : 1}
                  sx={fieldSx}
                />
                {key === "hisEmail" && (
                  <EmailDeliverabilityStatus status={hisEmailStatus} />
                )}
                {key === "herEmail" && (
                  <EmailDeliverabilityStatus status={herEmailStatus} />
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setLeadContactModalOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={leadContactSaving}
            onClick={() => {
              if (shouldBlockOnInvalid(hisEmailStatus, herEmailStatus)) {
                toast.error("Please enter a valid email.");
                return;
              }
              handleSaveLeadContact();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2.5,
              px: 3,
              bgcolor: "#2563eb",
              "&:hover": { bgcolor: "#1d4ed8" },
            }}
          >
            {leadContactSaving ? "Saving…" : "Save Contact"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Projected Retreat Report Preview */}
      <Dialog
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        fullWidth
        maxWidth="lg"
        aria-labelledby="projected-report-title"
        className="projected-report-dialog"
        PaperProps={{ className: "projected-report-paper", sx: projectedPageFontSx }}
      >
        <DialogTitle
          id="projected-report-title"
          className="projected-report-print-hide"
          sx={{ pb: 1 }}
        >
          Projected Retreat Report Preview
        </DialogTitle>
        <DialogContent id="projected-report-dialog-content" sx={{ bgcolor: "#e9eef3", py: 2 }}>
          {(dirty || !retreatId) && (
            <Alert
              severity="warning"
              className="projected-report-print-hide"
              sx={{ mb: 1.5 }}
            >
              This preview includes unsaved form changes. Save corrections before
              printing or saving the official record.
            </Alert>
          )}
          <Box id="projected-report-scroll-frame" sx={{ overflow: "auto", pb: 1 }}>
            <Box
              id="projected-retreat-report"
              component="article"
              aria-label="Projected Retreat one-page report"
              sx={{
                width: "8.5in",
                minHeight: "11in",
                mx: "auto",
                p: "0.42in",
                bgcolor: "#fff",
                color: "#172b4d",
                boxShadow: "0 10px 32px rgba(23, 43, 77, .16)",
                boxSizing: "border-box",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
                "&, & *": {
                  WebkitPrintColorAdjust: "exact",
                  printColorAdjust: "exact",
                },
                "@media print": {
                  position: "static",
                  width: "8.5in",
                  minHeight: "10.95in",
                  m: 0,
                  p: "0.35in",
                  boxShadow: "none",
                  bgcolor: "#fff !important",
                  color: "#172b4d !important",
                },
              }}
            >
              <Box
                className="projected-report-banner"
                sx={{
                  bgcolor: "#102f52",
                  color: "#fff",
                  borderRadius: 1.5,
                  p: 2,
                  mb: 1.5,
                  WebkitPrintColorAdjust: "exact",
                  printColorAdjust: "exact",
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      className="projected-report-kicker"
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: ".16em",
                        textTransform: "uppercase",
                        color: "#d6b45c",
                      }}
                    >
                      LOVESTRONG Marriage
                    </Typography>
                    <Typography
                      className="projected-report-title"
                      component="h1"
                      sx={{
                        mt: 0.25,
                        fontSize: 22,
                        lineHeight: 1.15,
                        fontWeight: 800,
                        color: "#ffffff",
                      }}
                    >
                      {parishDisplayName(selectedParish) || "Projected retreat"}
                    </Typography>
                    <Typography
                      className="projected-report-sub"
                      sx={{ mt: 0.55, fontSize: 12, color: "#dce8f3" }}
                    >
                      {tentativeFriday && tentativeSunday
                        ? formatWeekendRange(tentativeFriday, tentativeSunday)
                        : "Exact weekend not yet selected"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Box
                      className="projected-report-chip"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        height: 24,
                        px: 1,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: ".06em",
                        bgcolor: "#d6b45c",
                        color: "#102f52",
                        borderRadius: 1,
                        WebkitPrintColorAdjust: "exact",
                        printColorAdjust: "exact",
                      }}
                    >
                      PROJECTED
                    </Box>
                    <Typography
                      className="projected-report-sub"
                      sx={{ mt: 0.65, fontSize: 10, color: "#dce8f3" }}
                    >
                      Retreat #{retreatId ?? "Draft"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.25fr .8fr .95fr",
                  gap: 1.1,
                  mb: 1.1,
                }}
              >
                <ReportSectionCard title="Retreat at a glance" accent>
                  <Stack spacing={0.8}>
                    <ReportDetailRow label="Type" value={retreatType || "Standard"} />
                    <ReportDetailRow
                      label="Season / language"
                      value={`${season && year ? `${season} ${year}` : "Not set"} · ${language || "English"}`}
                    />
                    <ReportDetailRow
                      label="Venue"
                      value={venue || "To be confirmed"}
                    />
                  </Stack>
                </ReportSectionCard>
                <ReportSectionCard title="Planning ownership">
                  <Stack spacing={0.8}>
                    <ReportDetailRow
                      label="Oversight"
                      value={SUPPORT_LABELS[supportType] || supportType}
                    />
                    <ReportDetailRow
                      label="Planning contact"
                      value={primarySupportName}
                    />
                  </Stack>
                </ReportSectionCard>
                <ReportSectionCard title="Readiness">
                  <Stack spacing={0.8}>
                    <ReportDetailRow
                      label="Lead review"
                      value={`${eligibilityCount} of ${ELIGIBILITY_ITEMS.length} items recorded`}
                    />
                    <ReportDetailRow
                      label="Setup handoff"
                      value={
                        String(transitionNote || "").trim()
                          ? "Note prepared"
                          : "Not yet prepared"
                      }
                    />
                  </Stack>
                </ReportSectionCard>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.1,
                  mb: 1.1,
                }}
              >
                <ReportSectionCard title="Lead Couple">
                  {selectedLead ? (
                    <Stack spacing={0.45}>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 800, color: "#172b4d" }}
                      >
                        {coupleShortName(selectedLead)}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: "#334e68" }}>
                        {[selectedLead.hisEmail, selectedLead.herEmail]
                          .filter(Boolean)
                          .join(" · ") || "Email not provided"}
                        {" · Phone not provided"}
                      </Typography>
                      {selectedLead.address && (
                        <Typography sx={{ fontSize: 10.5, color: "#334e68" }}>
                          {selectedLead.address}
                        </Typography>
                      )}
                      {selectedLead.notes && (
                        <Typography
                          sx={{
                            fontSize: 10,
                            lineHeight: 1.35,
                            color: "#5b6b7e",
                            fontStyle: "italic",
                          }}
                        >
                          {selectedLead.notes}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: 11, color: "#5b6b7e" }}>
                      Not assigned
                    </Typography>
                  )}
                </ReportSectionCard>
                <ReportSectionCard title="Location & planning">
                  <Stack spacing={0.8}>
                    <ReportDetailRow
                      label="Official parish address"
                      value={
                        parishAddressLine(selectedParish) || "Not available"
                      }
                    />
                    <ReportDetailRow
                      label="Planning context"
                      value={
                        planningNotes || "No planning context recorded."
                      }
                    />
                  </Stack>
                </ReportSectionCard>
              </Box>

              {(showSeedFunding || showOutreachBudget) && (
                <ReportSectionCard
                  title={
                    showOutreachBudget
                      ? "Outreach sponsorship & tentative budget"
                      : "First-retreat seed funding"
                  }
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: 1,
                    }}
                  >
                    <ReportDetailRow
                      label="Status"
                      value={showOutreachBudget ? sponsorshipStatus : seedStatus}
                    />
                    <ReportDetailRow
                      label="Approval / funding contact"
                      value={
                        (showOutreachBudget
                          ? sponsorshipContact
                          : seedFundingContact) || "To be confirmed"
                      }
                    />
                    <ReportDetailRow
                      label="Execution contact"
                      value={
                        (showOutreachBudget
                          ? sponsorshipExecution
                          : seedExecutionContact) || "To be confirmed"
                      }
                    />
                    {showSeedFunding && (
                      <ReportDetailRow
                        label="Tentative seed donation amount"
                        value={
                          seedAmount === ""
                            ? "Not proposed"
                            : formatCurrency(Number(seedAmount) || 0)
                        }
                      />
                    )}
                    {showOutreachBudget &&
                      BUDGET_LINE_KEYS.filter((line) => budget[line.key]).map(
                        (line) => (
                          <ReportDetailRow
                            key={line.key}
                            label={line.label}
                            value={`$${budget[line.key]}`}
                          />
                        ),
                      )}
                    {showOutreachBudget && (
                      <ReportDetailRow
                        label="Tentative total"
                        value={
                          budgetTotal == null
                            ? "Not yet estimated"
                            : formatCurrency(budgetTotal)
                        }
                      />
                    )}
                    {showOutreachBudget && (
                      <ReportDetailRow
                        label="Policy check"
                        value="Confirm current pro-bono donation split; the February 5, 2025 record was 50/50."
                      />
                    )}
                    {showSeedFunding && language === "Spanish" && (
                      <ReportDetailRow
                        label="Policy check"
                        value="Confirm first-retreat eligibility versus a later second-language expansion."
                      />
                    )}
                  </Box>
                  {(seedNote || sponsorshipNote || budgetNote) && (
                    <Typography
                      sx={{
                        mt: 0.8,
                        fontSize: 10,
                        lineHeight: 1.35,
                        color: "#5b6b7e",
                      }}
                    >
                      {[seedNote, sponsorshipNote, budgetNote]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  )}
                </ReportSectionCard>
              )}

              {(String(transitionNote || "").trim() ||
                ELIGIBILITY_ITEMS.some((item) =>
                  eligibility[item.id]?.note?.trim(),
                )) && (
                <Box sx={{ mt: 1.1 }}>
                  <ReportSectionCard title="Follow-up record">
                    {String(transitionNote || "").trim() && (
                      <ReportDetailRow
                        label="Setup handoff note"
                        value={transitionNote}
                      />
                    )}
                    {ELIGIBILITY_ITEMS.filter((item) =>
                      eligibility[item.id]?.note?.trim(),
                    ).map((item) => (
                      <Box key={item.id} sx={{ mt: 0.75 }}>
                        <ReportDetailRow
                          label="Eligibility exception / supporting note"
                          value={eligibility[item.id].note}
                        />
                      </Box>
                    ))}
                  </ReportSectionCard>
                </Box>
              )}

              <Divider sx={{ mt: 1.35, mb: 0.8 }} />
              <Stack
                direction="row"
                sx={{ justifyContent: "space-between", gap: 2 }}
              >
                <Typography sx={{ fontSize: 10, color: "#5b6b7e" }}>
                  Prepared by {actorPayload().actorName} ·{" "}
                  {new Date().toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#5b6b7e",
                    textAlign: "right",
                  }}
                >
                  Planning record only · Funding and access require separate
                  confirmation
                </Typography>
              </Stack>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className="projected-report-print-hide">
          <Button onClick={() => setReportModalOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<Printer size={16} />}
            disabled={dirty || !retreatId || printingReport}
            onClick={async () => {
              setPrintingReport(true);
              try {
                await exportProjectedRetreatReport(
                  [
                    "Projected-Retreat-Report",
                    parishDisplayName(selectedParish),
                    retreatId,
                  ]
                    .filter(Boolean)
                    .join("-"),
                  "print",
                );
              } finally {
                setPrintingReport(false);
              }
            }}
          >
            {printingReport ? "Preparing…" : "Print"}
          </Button>
          <Button
            variant="contained"
            startIcon={<Download size={16} />}
            disabled={dirty || !retreatId || printingReport}
            onClick={async () => {
              setPrintingReport(true);
              try {
                await exportProjectedRetreatReport(
                  [
                    "Projected-Retreat-Report",
                    parishDisplayName(selectedParish),
                    retreatId,
                  ]
                    .filter(Boolean)
                    .join("-"),
                  "save",
                );
              } finally {
                setPrintingReport(false);
              }
            }}
          >
            {printingReport ? "Preparing PDF…" : "Save as PDF"}
          </Button>
        </DialogActions>
        <Box
          component="style"
          dangerouslySetInnerHTML={{
            __html: `@media print {
            @page { size: Letter portrait; margin: 0; }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body > *:not(.projected-report-dialog) { display: none !important; }
            .projected-report-dialog { position: static !important; inset: auto !important; }
            .projected-report-dialog .MuiBackdrop-root,
            .projected-report-print-hide { display: none !important; }
            .projected-report-dialog .MuiDialog-container {
              display: block !important;
              height: auto !important;
              min-height: 0 !important;
            }
            .projected-report-paper {
              position: static !important;
              width: 8.5in !important;
              max-width: none !important;
              max-height: none !important;
              margin: 0 !important;
              overflow: visible !important;
              box-shadow: none !important;
              background: #fff !important;
            }
            #projected-report-dialog-content,
            #projected-report-scroll-frame {
              padding: 0 !important;
              overflow: visible !important;
              background: #fff !important;
              max-height: none !important;
              height: auto !important;
            }
            #projected-retreat-report,
            #projected-retreat-report * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            #projected-retreat-report {
              background: #fff !important;
              color: #172b4d !important;
              box-shadow: none !important;
            }
            .projected-report-banner {
              background: #102f52 !important;
              color: #fff !important;
            }
            .projected-report-kicker { color: #d6b45c !important; }
            .projected-report-title { color: #fff !important; }
            .projected-report-sub { color: #dce8f3 !important; }
            .projected-report-chip {
              background: #d6b45c !important;
              color: #102f52 !important;
            }
            .projected-report-card-title {
              background: #edf3f8 !important;
              color: #14365d !important;
            }
            .projected-report-card-title.is-accent {
              background: #14365d !important;
              color: #fff !important;
            }
          }`,
          }}
        />
      </Dialog>

      {/* Transition Confirmation Modal */}
      <Dialog
        open={setupConfirmModalOpen}
        onClose={transitioning ? undefined : () => setSetupConfirmModalOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="setup-confirmation-title"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle id="setup-confirmation-title" sx={{ fontWeight: 800, color: "#0f172a" }}>
          Start Retreat Setup?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1,
              }}
            >
              <HandoffSummaryCard
                label="Retreat"
                title={parishDisplayName(selectedParish) || "Projected retreat"}
              >
                {selectedParish
                  ? [selectedParish.city, selectedParish.state]
                      .filter(Boolean)
                      .join(", ")
                  : ""}
              </HandoffSummaryCard>
              <HandoffSummaryCard
                label="Retreat Type"
                title={retreatType || "—"}
              >
                Planning horizon classification
              </HandoffSummaryCard>
              <HandoffSummaryCard label="Weekend" title={weekendLabel}>
                {seasonYearLabel}
              </HandoffSummaryCard>
              <HandoffSummaryCard
                label="Planning Oversight"
                title={SUPPORT_LABELS[supportType] || supportType}
              >
                Oversight remains distinct from the record actor and Lead
                Couple.
              </HandoffSummaryCard>
              <HandoffSummaryCard
                label="Lead Couple"
                title={
                  selectedLead ? coupleShortName(selectedLead) : "Not assigned"
                }
              >
                Eligibility reviewed by {actorName}
              </HandoffSummaryCard>
              <HandoffSummaryCard
                label="Planning Contact"
                title={
                  primarySupportName === "Not assigned"
                    ? "Not assigned"
                    : primarySupportName
                }
              >
                Planning Contact access assignment prepared
              </HandoffSummaryCard>
              <HandoffSummaryCard label="Current Status" title="Projected">
                No lifecycle change has occurred yet.
              </HandoffSummaryCard>
              <HandoffSummaryCard
                label="New Status"
                title="Retreats in Setup"
              >
                Applied only after explicit confirmation.
              </HandoffSummaryCard>
              <HandoffSummaryCard
                label="Notification recipients"
                title="Lead Couple and Planning Contact"
              >
                {recipientEmails}
              </HandoffSummaryCard>
            </Box>
            <Alert severity="warning">
              The same retreat ID will move to Setup and Retreat History will
              record it. Lead and Planning Contact access assignments will be
              prepared with the transition. Retreat-specific access is intended
              to begin in Setup, but this prototype does not activate access.
              The notification is prepared for the listed recipients, but no
              production email is delivered.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setSetupConfirmModalOpen(false)}
            disabled={transitioning}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={transitioning}
            onClick={handleConfirmTransition}
            sx={projectedSaveButtonSx}
          >
            {transitioning ? "Moving…" : "Confirm Start Retreat Setup"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
